import { PriceChangeAlert } from '@/components/products/price-changes/PriceChangeAlert'
import { InfoNoticeAction } from '@/components/patterns/info-notice'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { locationFilters, locationQueries } from '@/lib/services/locations/locations.query'
import { useStore } from '@/lib/services/stores/stores.query'
import {
  DESIGNERS_CHOICE_ONLY_EMPTY_COPY,
  hasAvailableDesignersChoiceProducts,
} from '@/lib/services/stores/stores.utils'
import {
  supportsAvailabilityChangesForOrganizationId,
  useAvailabilityChanges,
} from '@/lib/services/availability-changes/availability-changes.query'
import {
  formatUpcomingChange,
  getAvailabilityAlertTitle,
  getAvailabilityAlertTone,
  getAvailabilityDashboardDescription,
  getUpcomingAvailabilityChangeOccurrences,
} from '@/lib/services/availability-changes/availability-changes.summary'
import { getAvailabilityChangeActiveOccurrenceForLocations } from '@/lib/services/availability-changes/availability-changes.utils'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { CalendarDays, ChevronRight } from 'lucide-react'

function getAvailabilityStatusLabel({ hasLocation, hasStore, isStoreActive }) {
  if (!hasStore) {
    return 'Store setup needed'
  }

  if (!isStoreActive) {
    return 'Store inactive'
  }

  if (!hasLocation) {
    return 'No active location'
  }

  return 'Accepting orders'
}

function getAvailabilitySummary({ currentRules, hasLocation, hasStore, isStoreActive }) {
  if (!hasStore) {
    return 'Create a store before accepting online pickup or delivery orders.'
  }

  if (!isStoreActive) {
    return 'Turn on the store before accepting online pickup or delivery orders.'
  }

  if (!hasLocation) {
    return 'Add an active location before accepting online pickup or delivery orders.'
  }

  if (currentRules.length === 0) {
    return "Pickup and delivery are available now. Store closures, pickup or delivery pauses, Designer's Choice-only periods, and temporary hours will show here."
  }

  return 'Review the current active availability changes and their schedules.'
}

export function useAvailabilityShortcutState(organizationId = null) {
  const { orgId: routeOrgId } = useParams({ strict: false })
  const orgId = organizationId ?? routeOrgId
  const ability = useAbility()
  const { data: store } = useStore()
  const hasStore = Boolean(store?.id)
  const isStoreActive = store?.is_active !== false
  const canListLocations = ability.can('list', 'locations')
  const { data: locations = [] } = useQuery({
    ...locationQueries.index({
      organizationId: orgId,
      filters: locationFilters().get(),
    }),
    enabled: canListLocations,
  })
  const activeLocations = locations.filter((location) => location?.is_active !== false)
  const defaultLocation =
    activeLocations.find((location) => location?.is_default) ?? activeLocations[0] ?? null
  const locationId = defaultLocation?.id ? String(defaultLocation.id) : ''
  const hasLocation = canListLocations ? locationId !== '' : true
  const canListAvailability =
    supportsAvailabilityChangesForOrganizationId(orgId) &&
    ability.can('list', 'availability-changes')
  const canEvaluateAvailability =
    hasStore && isStoreActive && canListAvailability && canListLocations
  const { data: availabilityChanges = [] } = useAvailabilityChanges({
    organizationId: orgId,
    filters: { past: false },
    enabled: canEvaluateAvailability,
  })
  const now = dayjs()
  const currentRules =
    canEvaluateAvailability && hasLocation
      ? availabilityChanges
          .map((rule) =>
            getAvailabilityChangeActiveOccurrenceForLocations(rule, now, activeLocations)
          )
          .filter(Boolean)
      : []
  const upcomingChanges =
    canEvaluateAvailability && hasLocation
      ? getUpcomingAvailabilityChangeOccurrences(availabilityChanges, now, activeLocations)
      : []
  const shouldShowSetupStatus = !hasStore || !isStoreActive || (canListLocations && !hasLocation)
  const statusLabel =
    canEvaluateAvailability || shouldShowSetupStatus
      ? getAvailabilityStatusLabel({
          hasLocation,
          hasStore,
          isStoreActive,
        })
      : 'Availability managed'
  const availabilitySummary =
    canEvaluateAvailability || shouldShowSetupStatus
      ? getAvailabilitySummary({
          currentRules,
          hasLocation,
          hasStore,
          isStoreActive,
        })
      : 'Availability changes limit delivery / pickup dates at checkout.'

  return {
    availabilitySummary,
    canListAvailability,
    currentRules,
    hasAvailableDesignersChoice: hasAvailableDesignersChoiceProducts(store),
    hasLocation,
    hasStore,
    isStoreActive,
    nextUpcomingLabel: upcomingChanges[0] ? formatUpcomingChange(upcomingChanges[0]) : null,
    statusLabel,
    store,
    upcomingChanges,
  }
}

function getShortcutAvailabilityState({ availabilitySummary, currentRuleCount, statusLabel }) {
  if (currentRuleCount > 0) {
    return {
      label: statusLabel,
      variant: 'warning',
      description: availabilitySummary,
    }
  }

  return {
    label: statusLabel,
    variant: statusLabel === 'Accepting orders' ? 'success' : 'muted',
    description: availabilitySummary,
  }
}

export function StoreStatusCard() {
  const path = usePathHelper()
  const {
    availabilitySummary,
    canListAvailability,
    currentRules,
    hasAvailableDesignersChoice,
    hasStore,
    isStoreActive,
    nextUpcomingLabel,
    statusLabel,
    upcomingChanges,
  } = useAvailabilityShortcutState()
  const showDesignersChoiceEmptyHint = hasStore && isStoreActive && !hasAvailableDesignersChoice
  const state = getShortcutAvailabilityState({
    availabilitySummary,
    currentRuleCount: currentRules.length,
    statusLabel,
  })
  const title = getAvailabilityAlertTitle({
    activeChanges: currentRules,
    upcomingChanges,
    state,
  })
  const summary = getAvailabilityDashboardDescription({
    hasActiveAvailabilityChanges: currentRules.length > 0,
    nextUpcomingLabel,
    state,
  })

  return (
    <PriceChangeAlert
      title={title}
      tone={getAvailabilityAlertTone(state)}
      icon={<CalendarDays />}
      action={
        hasStore && canListAvailability ? (
          <InfoNoticeAction {...path.availability.index().link}>
            Manage availability
            <ChevronRight className="size-3.5" />
          </InfoNoticeAction>
        ) : null
      }
    >
      <Typography variant="label" className="block">
        {summary}
      </Typography>
      {showDesignersChoiceEmptyHint ? (
        <Typography variant="supporting" tone="warning" className="mt-2 block">
          {DESIGNERS_CHOICE_ONLY_EMPTY_COPY}
        </Typography>
      ) : null}
    </PriceChangeAlert>
  )
}
